// src/contexts/BookingContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const STORAGE_KEY = 'bookingData';

const BookingContext = createContext();

function readStoredBooking() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export const BookingProvider = ({ children }) => {
  const stored = typeof window !== 'undefined' ? readStoredBooking() : null;

  const [selectedServices, setSelectedServices] = useState(
    () => stored?.selectedServices || []
  );
  const [selectedSalon, setSelectedSalon] = useState(
    () => stored?.selectedSalon || null
  );
  const [selectedDate, setSelectedDate] = useState(
    () => stored?.selectedDate || null
  );
  const [selectedTime, setSelectedTime] = useState(
    () => stored?.selectedTime || null
  );
  const [loading, setLoading] = useState(false);

  // Persister à chaque changement (salon inclus pour survivre au refresh)
  useEffect(() => {
    const bookingData = {
      selectedServices,
      selectedSalon,
      selectedSalonId: selectedSalon?.id || null,
      selectedDate,
      selectedTime,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bookingData));
  }, [selectedServices, selectedSalon, selectedDate, selectedTime]);

  const checkSalonChange = (newSalon) => {
    if (!selectedSalon) return true;
    if (selectedSalon.id === newSalon.id) return true;
    return false;
  };

  const addService = (service) => {
    if (selectedServices.find((s) => s.id === service.id)) return;
    setSelectedServices((prev) => [...prev, service]);
  };

  const replaceServicesWithNewSalon = (newSalon, newService) => {
    setSelectedSalon(newSalon);
    setSelectedServices([newService]);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const setSalon = (salon) => {
    setSelectedSalon(salon);
  };

  const removeService = (serviceId) => {
    setSelectedServices((prev) => prev.filter((s) => s.id !== serviceId));
  };

  const getTotalDuration = () =>
    selectedServices.reduce((total, service) => total + parseInt(service.duration, 10), 0);

  const getTotalPrice = () =>
    selectedServices.reduce((total, service) => total + parseFloat(service.price), 0);

  const clearSelection = () => {
    setSelectedServices([]);
    setSelectedSalon(null);
    setSelectedDate(null);
    setSelectedTime(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const clearServices = clearSelection;

  return (
    <BookingContext.Provider
      value={{
        selectedServices,
        selectedSalon,
        selectedDate,
        selectedTime,
        loading,
        setSelectedDate,
        setSelectedTime,
        addService,
        removeService,
        setSalon,
        getTotalDuration,
        getTotalPrice,
        clearSelection,
        clearServices,
        checkSalonChange,
        replaceServicesWithNewSalon,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking doit être utilisé dans un BookingProvider');
  }
  return context;
};
